import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, PropType, ref, inject, onMounted, watch } from "vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import SingleSelect from "@/components/ui/dropdowns/single-select.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import ContactsPeekPopup from "@/components/contacts/contacts-peek-popup.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'lookup-single-select',
  props: {
  /**
   * The numeric value used with `v-model` to bind the component state. This typically represents an ID or a selected value.
   * @type {number}
   * @required
   */
  modelValue: {
    type: Number,
    required: true,
  },
  /**
   * The source lookup table from which data is fetched. This is used to retrieve a list of items from the API.
   * @type {LookupTableSource}
   * @required
   */
  lookupSource: {
    type: Number as PropType<LookupTableSource>,
    required: true,
  },
  /**
   * The label displayed for the control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * Determines whether the inactive items are included.
   * @type {boolean}
   * @default false
   */
  includeInactiveItems: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the specific control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the input control is required.
   * @type {boolean}
   * @default false
   */
  requiredInput: {
    type: Boolean,
    default: false,
  },
  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    required: false,
    default: "",
  },
  /**
   * Determines whether to show the search textbox.
   * @type {boolean}
   * @default false
   */
  showSearch: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to allow zero value.
   * @type {boolean}
   * @default false
   */
  allowZeroValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show the clear button.
   * @type {boolean}
   * @default false
   */
  showClearButton: {
    type: Boolean,
    default: false,
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * Determines whether to allow to reset validation.
   * @type {boolean}
   * @default false
   */
  setValid: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to update TableSource on id change.
   * @type {boolean}
   * @default false
   */
  updateTableSourceOnIdChange: {
    type: Boolean,
    default: false,
  },
  /**
   * Indicates whether the All option should be added to the drop down
   * @type {boolean}
   * @default false
   */
  displayAllOption: {
    type: Boolean,
    default: false,
  },
  /**
   * Ids that will be excluded from the dropdown list
   * @type {number[]}
   * @default []
   */
  idsToHide: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  /**
   * groupId
   * @type Number
   * @default -99 //conNotSet
   */
  groupId: {
    type: Number,
    default: -99, //conNotSet
  },
  /**
   * Determines whether to display Peek.
   * @type {boolean}
   * @default false
   */
  displayPeek: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Emitted when the `modelValue` is updated, typically triggered when a selection changes.
   * @event update:modelValue
   * @param {Number} value - The new value to be updated in the model.
   */
  "update:modelValue",
  /**
   * Triggered when the component is clicked.
   * This event can be used to handle user interactions or trigger actions in the parent component.
   * @event iconClicked
   */
  "iconClicked",
  /**
   * Emitted when the value of the selection changes, with the updated description.
   * @event valueChanged
   * @param {string} value - The description of the selected value (or empty string if no selection).
   */
  "valueChanged",
],
  setup(__props, { emit }) {

const props = __props;





const selectedValueId = computed({
  get: () => props.modelValue,
  set: (value: number) => emit("update:modelValue", value),
});

const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const lookupItems = ref<IDDescriptionPair[]>([]);
const showPeek = ref(false);
const peekModalKey = ref(0);
const contactName = ref("");

onMounted(async () => {
  await getLookupItems();
});

const incrementPeekModalKey = () => {
  peekModalKey.value++;
};

watch(
  () => props.lookupSource,
  async (newValue: LookupTableSource, oldValue: LookupTableSource) => {
    if (newValue === oldValue) {
      return;
    }

    selectedValueId.value = 0;
    await getLookupItems();
  },
);

const getLookupItems = async () => {
  lookupItems.value = [];
  // Call api for a list of lookup items to display. These aren't cached as there may be a lot of data

  lookupItems.value = await acquaintApiClient.httpPost<IDDescriptionPair[]>(
    `app/system/lookup`,
    {
      sourceId: props.lookupSource,
      groupId: props.groupId,
      includeInactive: props.includeInactiveItems,
      selectedRecordId: selectedValueId.value,
    },
  );

  if (props.displayAllOption) {
    lookupItems.value = lookupItems.value.filter((item) => item.id !== -1);
    lookupItems.value.push({ id: -1, description: "All" });
  }

  // This filters out any ids that should be hidden
  lookupItems.value = lookupItems.value.filter(
    (item) => !props.idsToHide.includes(item.id),
  );
};

const iconClicked = (selectedId: number) => {
  const selectedItem: IDDescriptionPair | undefined = lookupItems.value.find(
    (item) => {
      return item.id == selectedId;
    },
  );
  emit("iconClicked", selectedItem?.id, selectedItem?.description);
};

watch(
  () => selectedValueId.value,
  async () => {
    if (props.updateTableSourceOnIdChange) {
      await getLookupItems();
    }

    let selectedValue = lookupItems.value.filter((idDescriptionPair) => {
      return idDescriptionPair.id == selectedValueId.value;
    });

    contactName.value =
      selectedValue.length > 0 ? selectedValue[0].description : "";
    incrementPeekModalKey();
    emit("valueChanged", contactName.value);
  },
);

watch(
  () => props.idsToHide,
  () => {
    lookupItems.value = lookupItems.value.filter(
      (item) => !props.idsToHide.includes(item.id),
    );
  },
);

const onPeekClick = () => {
  showPeek.value = true;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(SingleSelect, {
      modelValue: selectedValueId.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedValueId).value = $event)),
      "data-source": lookupItems.value,
      readOnly: props.readOnly,
      allowZeroValue: props.allowZeroValue,
      showClearButton: props.showClearButton,
      label: __props.label,
      requiredInput: props.requiredInput,
      validationGroup: props.validationGroup,
      showSearch: props.showSearch,
      displayPeek: __props.displayPeek,
      onIconClicked: iconClicked,
      width: __props.width,
      setValid: props.setValid,
      onPeekClick: onPeekClick
    }, null, 8, ["modelValue", "data-source", "readOnly", "allowZeroValue", "showClearButton", "label", "requiredInput", "validationGroup", "showSearch", "displayPeek", "width", "setValid"]),
    (_openBlock(), _createBlock(ContactsPeekPopup, {
      key: peekModalKey.value,
      modelValue: showPeek.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showPeek).value = $event)),
      contactId: selectedValueId.value,
      title: contactName.value
    }, null, 8, ["modelValue", "contactId", "title"]))
  ], 64))
}
}

})